const CONSTANTS = module.exports = {};

// The append-only ledger's event catalogue. `credit_line_events.type` reads Object.values(...).
// One type dimensioned by `meta` is the standing pattern here: a sibling MONTHLY_OVERRIDE_CHANGE was
// considered and rejected, because both writes are "finance moved this cap's money" and a second type
// would need the same fields plus an exception in every consumer that already handles LIMIT_CHANGE.
CONSTANTS.CREDIT_LINE_EVENT_TYPE = {
    DEBIT: 'DEBIT',
    CREDIT: 'CREDIT',
    LIMIT_CHANGE: 'LIMIT_CHANGE',
    THRESHOLD_CHANGE: 'THRESHOLD_CHANGE',
    MODE_CHANGE: 'MODE_CHANGE',
    // M22. A rename moves no counter, but a shared pool's NAME is how a human identifies it in an
    // alert, a preflight and a picker — so a silent rename makes the history of that pool unreadable
    // to the only people who read it. Its own type rather than a `meta` dimension of LIMIT_CHANGE:
    // the sibling that was REJECTED (MONTHLY_OVERRIDE_CHANGE) was rejected because both writes were
    // 'finance moved this cap's money' and fed OB-9's crossing identically. A name is neither.
    NAME_CHANGE: 'NAME_CHANGE',
    GATE_REFUSED: 'GATE_REFUSED',
    CAP_BLOCKED: 'CAP_BLOCKED',
    CAP_UNBLOCKED: 'CAP_UNBLOCKED',
    // OB-57/OB-58's SOFT-DELETE. Whole-cap, like CAP_BLOCKED/CAP_UNBLOCKED. A delete is idempotent and
    // writes at most one of these per delete.
    CAP_DELETED: 'CAP_DELETED',
    // Feature #65 — the RESTORE of a deleted pool (`credit_line_restore.js`). Idempotent: at most one per
    // restore, never on a pool that is already live. `meta.companies` carries the same scope shape
    // CAP_DELETED does — the holders the restore re-gates.
    CAP_RESTORED: 'CAP_RESTORED',
    ATTACHMENT_CHANGE: 'ATTACHMENT_CHANGE',
    RECONCILIATION: 'RECONCILIATION',
    // HISTORICAL ONLY — nothing writes it since the user ruling of 2026-10-05 withdrew OB-17 §4.2 (a
    // re-enable no longer resets exposure). Kept because existing ledger rows carry it and the trader UI
    // still renders them. Was: the counter returned to UNKNOWN on re-enablement. `meta` carries the
    // discarded buckets as a PER-MONTH map, never a total: a total cannot answer 'which month were we
    // over?', cannot be compared against a later rebuild month by month, and under M5 per month is the
    // only question anyone asks of this row.
    EXPOSURE_RESET: 'EXPOSURE_RESET',
    // OB-17 §7. The kill switch's flip is unauditable by construction — it rides a wholesale settings
    // assign — so these record that an OBSERVER SAW the state, never that a person set it. That is why
    // they say 'observed', and why `actor` on them is the system and never a human.
    CREDIT_LINE_DISABLED_OBSERVED: 'CREDIT_LINE_DISABLED_OBSERVED',
    CREDIT_LINE_ENABLED_OBSERVED: 'CREDIT_LINE_ENABLED_OBSERVED',
    // A PERSON flipped the kill switch through the checked write (`applyCreditLineKillSwitch`): the
    // deliberate operator toggle, with its `actor` and `reason` — never the `*_OBSERVED` pair above,
    // which records what a watcher saw and names the system. One row per pool the company reaches, on
    // a real transition only; `meta: { previously_disabled, disabled }`. Moves no money: no amount, no
    // `bucket_month`, no exposure change (user ruling 2026-10-05).
    CREDIT_LINE_DISABLED: 'CREDIT_LINE_DISABLED',
    CREDIT_LINE_ENABLED: 'CREDIT_LINE_ENABLED',
};

// Why exposure came back, on a CREDIT row. The enum is complete; the call sites are not — SETTLEMENT
// and CONVERSION_ROLLBACK have no emitter in this phase, and declaring them here is deliberate so a
// later phase adds a call site rather than an enum member.
CONSTANTS.CREDIT_LINE_RELEASE_CAUSE = {
    SETTLEMENT: 'SETTLEMENT',
    CANCELLATION: 'CANCELLATION',
    BOOKING_FAILED: 'BOOKING_FAILED',
    CONVERSION_ROLLBACK: 'CONVERSION_ROLLBACK',
    AMENDMENT: 'AMENDMENT',
    // A PERSON DECIDED. Emitted by `packages/credit-line/credit_line_manual_release.js`, the operator
    // release endpoint — this is the "later phase adds a call site rather than an enum member" the
    // note above promised, taken up rather than worked around. A sibling such as
    // `MANUAL_OPERATOR_RELEASE` was considered and REJECTED: minting a second member beside an
    // already-reserved one that means the same thing is precisely the drift the note exists to
    // prevent, and it would leave `MANUAL` permanently unemitted and permanently ambiguous.
    //
    // DISTINCT FROM CANCELLATION AND FROM ORPHAN_SWEEP, and that distinction is the point. A
    // cancellation is a statement about the customer, a swept release a statement about a process that
    // died, and this one a statement about an operator — whose identity is on the row's `actor` and
    // whose justification is on its `reason`. The three must never be readable as each other.
    MANUAL: 'MANUAL',
    // OB-4 §6.2. The orphan sweep's own cause, and its own member rather than MANUAL or CANCELLATION:
    // a swept release is the ONE release nobody asked for, and the only evidence that the exposure was
    // ever stranded is this row. Folded into CANCELLATION it would read in the history as a booking
    // that was cancelled — which is a statement about the customer, not about a process that died —
    // and folded into MANUAL it would claim a person made a decision nobody made. Both readings hide
    // the number the sweep exists to produce: how much exposure is being reclaimed automatically.
    ORPHAN_SWEEP: 'ORPHAN_SWEEP',
};

// Why a booking was turned away, on a GATE_REFUSED row. This distinction is what lets the
// would-have-blocked measurement exclude manual blocks — a manual block refuses in BOTH modes, so
// counting it as evidence that a threshold would have bound is counting the wrong thing.
CONSTANTS.CREDIT_LINE_REFUSAL_CAUSE = {
    THRESHOLD: 'THRESHOLD',
    MANUAL_BLOCK: 'MANUAL_BLOCK',
};

// FU-082 (resolved 2026-10-07). The refusal a booking or request gets when the credit-line GATE ITSELF
// failed on an enforcing cap, or on a cap whose mode could not be determined. Not a cap decision — the
// line may well have room — so it is a RETRYABLE refusal (HTTP 503), never a GATE_REFUSED row and never
// a member of CREDIT_LINE_REFUSAL_CAUSE above. The wire code's single source is `arbitrip-common`'s
// CreditLineConstants, because web and mobile key their dialog off the same string.
//
// The message is the APPROVED customer-facing copy (EN). Every server surface that answers this code
// sends exactly this text; the clients render their own translated copy keyed off the code.
CONSTANTS.CREDIT_LINE_CHECK_UNAVAILABLE_CODE =
    require('arbitrip-common/general/constants/CreditLineConstants').ERROR_CONSTANTS.CREDIT_LINE_CHECK_UNAVAILABLE;
CONSTANTS.CREDIT_LINE_CHECK_UNAVAILABLE_MESSAGE = "We couldn't check your credit line. Please try again or pay by card.";

// Which LEVEL of the cap fallback chain answered, recorded on the decision so a refusal stays
// re-justifiable after the override that caused it has been edited. A DIFFERENT axis from
// `attachment_source`, which says which attachment BRANCH supplied the cap — one booking is true of
// both at once, which is why they are two fields and not a nine-member cross product.
CONSTANTS.CREDIT_LINE_CAP_SOURCE = {
    MONTHLY_OVERRIDE: 'MONTHLY_OVERRIDE',
    DEFAULT_MONTHLY_LIMIT: 'DEFAULT_MONTHLY_LIMIT',
    LEGACY_LIMIT: 'LEGACY_LIMIT',
};

// Which cap dimension finance MOVED, on a LIMIT_CHANGE row. Same three spellings as the axis above
// and a different question — that one is "what did we judge against", this one is "what was edited".
// LEGACY_LIMIT names the pre-existing `credit_lines.limit` writers: no new code writes that field,
// and emitting the event is not writing it.
CONSTANTS.CREDIT_LINE_CAP_FIELD = {
    DEFAULT_MONTHLY_LIMIT: 'DEFAULT_MONTHLY_LIMIT',
    MONTHLY_OVERRIDE: 'MONTHLY_OVERRIDE',
    LEGACY_LIMIT: 'LEGACY_LIMIT',
};

// Which date produced the bucket month. PROVENANCE ONLY — it selects no field and no behaviour; it
// exists so a human can read a ledger row without re-deriving isReservationRefundable.
CONSTANTS.CREDIT_LINE_LEG = {
    NON_REFUNDABLE: 'non_refundable',
    REFUNDABLE: 'refundable',
};

// M23 / OB-20. WHICH STAGE a `DEBIT` was taken at, stamped in `meta` on every row.
//
// THE STAGE NO LONGER SELECTS ANYTHING, and every new row stamps `BOOKING`. A request evaluates the
// cap and reserves nothing (`gateAccountRequest`), so a `DEBIT` means "a booking is about to be made"
// again, and the readers that once branched on the stamp — the orphan sweep's predicate, the
// admission limit — no longer consult it.
//
// KEPT ANYWAY, for two reasons: rows written while the request stage existed carry `REQUEST` and must
// stay readable, and stamping the stage costs nothing while a row that does not say where it came from
// cannot be told apart later. Treat it as PROVENANCE, never as a branch — a reader that keys behaviour
// off it is rebuilding the split this removed.
//
// STAMPED, NOT COUNTED SEPARATELY. A stage-keyed counter beside `exposure.count_by_month` was
// considered and rejected: it needs its own guarded `$inc`, its own reset semantics under
// `EXPOSURE_RESET` and its own correctness story on every release path — a lot of money-path machinery
// for a number no surface reads yet. The ledger already holds every row durably, so a split that is
// genuinely needed is a QUERY rather than new state. `count_by_month` therefore counts RESERVATIONS
// HOLDING EXPOSURE, which is what it always counted — and that is the definition to keep quoting even
// while it happens to coincide with "bookings" again.
//
// A row with NO `meta.stage` predates OB-20 and is a BOOKING. The absence means "before this existed",
// never "not a request".
CONSTANTS.CREDIT_LINE_EXPOSURE_STAGE = {
    REQUEST: 'REQUEST',
    BOOKING: 'BOOKING',
};

// The cap rule in force when a decision was made, stamped on EVERY row including config events and
// GATE_REFUSED. It is what keeps history interpretable across the rule change that has already
// happened: a row written before the per-month rule means a WINDOW SUM by its exposure figures, and
// nothing else on the row distinguishes the two eras.
//
// WINDOW_SUM is READ-ONLY — the historical value, never written again. The struck three-value set
// also carried ALL_OUTSTANDING; it was never the live value, so no row can carry it, and declaring it
// would mint a member that can never be read. Do not "restore" it.
CONSTANTS.CREDIT_LINE_POLICY_RULE = {
    PER_MONTH: 'PER_MONTH',
    WINDOW_SUM: 'WINDOW_SUM',
};

// The rule every row written from now on is judged under.
CONSTANTS.CREDIT_LINE_CURRENT_POLICY_RULE = CONSTANTS.CREDIT_LINE_POLICY_RULE.PER_MONTH;

// How far ahead of the current month finance may open or close a month.
//
// **VALUE DELIBERATELY UNSET — it is an open finance question (FU-047), not an engineering one.**
// The constant is minted so there is one place to answer it; a number invented here would become the
// rule by accident. While it is null the horizon rule does not fire, and the past-month rule — which
// IS decided — still does. It is a code constant rather than a per-cap field for the same reason the
// cap resolver is one helper: one rule for every cap, so a stored copy cannot disagree with it.
CONSTANTS.CREDIT_LINE_MAX_OVERRIDE_HORIZON_MONTHS = null;

// `ATTACHMENT_CHANGE.meta` — one type dimensioned by scope and action, for the same reason the
// catalogue above rejected a sibling type: every consumer handles all four actions identically.
CONSTANTS.CREDIT_LINE_ATTACHMENT_SCOPE = {
    COMPANY: 'COMPANY',
    IDENTITY: 'IDENTITY',
};

CONSTANTS.CREDIT_LINE_ATTACHMENT_ACTION = {
    ATTACH: 'ATTACH',
    DETACH: 'DETACH',
    ENABLE: 'ENABLE',
    DISABLE: 'DISABLE',
};

// OB-9's three alert classes (`08-ob-9` §4.4). The tail class was DELETED by M6 — the condition it
// fired on cannot occur — so there are three and not four, and no member is minted for it here.
//
// CAP_BLOCKED / CAP_UNBLOCKED deliberately spell the same words as the ledger event types above and
// are a DIFFERENT axis: those record that a cap was blocked, these record that somebody was told.
//
// ---------------------------------------------------------------------------------------------
// THE TWO BLOCK CLASSES ARE EXEMPT FROM THRESHOLD-SHAPED TREATMENT. IF YOU ADD A CONSUMER OF THIS
// ENUM, CHECK IT AGAINST THEM BEFORE YOU SHIP IT.
//
// A `THRESHOLD_CROSSED` alert reports a COMPUTED NUMBER: it can be silenced when the number is real
// for nobody, it is coalesced by month, it carries a "nothing was refused" disclaimer while the cap
// is in shadow, and it escalates only when the current month is over its block threshold. A block
// alert is none of those things. It reports a HUMAN DECISION that took effect in both modes, names
// no month at all, and is the most urgent thing this system sends.
//
// Four properties follow, and each has been got wrong at least once:
//
//   - SEVERITY — a block is the severe class. It is NOT `urgent`, which is a threshold concept and
//     false for both block classes by construction, so keying colour or prefix on `urgent` alone
//     under-reports it (F-065).
//   - THE SHADOW DISCLAIMER — never on a block, in the headline or the body. A manual block binds in
//     both modes, so "nothing was refused" on one is false (F-051, F-064).
//   - EMPTY `months` — a block names no month BY DESIGN (OB-15 §3.1), so a guard that treats an empty
//     month list as "nothing happened" drops the whole alert (F-055).
//   - SILENCING — a crossing on a cap whose every company is disabled is real for nobody; a block on
//     that same cap is the one message finance most needs (F-052).
//
// THE FAILURE IS NOT ABSTRACT. It is a manual block rendered as a routine measurement that refused
// nothing — read by finance as "the customer is still open" while the gate is turning them away, or
// the reverse. Every instance above shipped green, because the block path had no producer until
// F-048 and each fix made the next one reachable.
//
// The full consumer enumeration — eleven sites across the derivation and both renderers — is in the
// OB-9 spec. This comment is the pointer; that table is the detail. The pointer lives here because
// this is the one file every consumer already imports.
// ---------------------------------------------------------------------------------------------
CONSTANTS.CREDIT_LINE_ALERT_CLASS = {
    THRESHOLD_CROSSED: 'THRESHOLD_CROSSED',
    CAP_BLOCKED: 'CAP_BLOCKED',
    CAP_UNBLOCKED: 'CAP_UNBLOCKED',
};

// How a month's DENOMINATOR was established, per OB-10 §6.3's `CapBasis`. It is the honesty field on
// every figure OB-9 renders for an elapsed month: `historical` means the cap came from the ledger via
// cap_in_effect_at(M, t), `current` that today's configuration is still the one that applied, and
// `unreconstructible` that the history needed to answer is gone — which renders like UNKNOWN and
// NEVER as a number. Substituting today's scalar for a settled month is the failure this forbids.
CONSTANTS.CREDIT_LINE_CAP_BASIS = {
    CURRENT: 'current',
    HISTORICAL: 'historical',
    UNRECONSTRUCTIBLE: 'unreconstructible',
};

// What caused the crossing OB-9 is reporting. `BOOKING` is a DEBIT moving one bucket; `CONFIG_CHANGE`
// is a LIMIT_CHANGE moving the denominator under one or more months with no booking involved. The two
// coalesce differently (§4.4a rule (b)) and that is the only reason the distinction is stored.
CONSTANTS.CREDIT_LINE_CROSSING_TRIGGER = {
    BOOKING: 'BOOKING',
    CONFIG_CHANGE: 'CONFIG_CHANGE',
};

// ---------------------------------------------------------------------------------------------
// THE ONE DISPLAY RULE FOR A UTILIZATION PERCENTAGE. Every surface that renders "how full is this
// month" reads it through here — the Slack table, both email tables and the email SUBJECT, the
// trader month rows, the book-wide monitor, and the customer portal.
//
// IT FLOORS, IT NEVER ROUNDS. `Math.round` is what BUG-44 was: a month at cap 112 with 100.55925
// used is 89.785% full against a block threshold of 90, so the gate correctly admitted bookings —
// and every surface printed "90%". An operator reading a figure EQUAL to the threshold beside a line
// that is not blocking concludes the gate is broken, and the next move is to lower the threshold
// until it "works". The display was the only thing that was wrong.
//
// Flooring makes the printed integer reach a rung EXACTLY when the real figure does: for an integer
// threshold T, floor(p) >= T if and only if p >= T. Rounding has no such property in either
// direction — it overstates below the rung (89.78 -> 90) and understates above it (99.5 -> 100 is
// fine, but 89.5 -> 90 is not). The ladder and `block_pct` are integers through the trader editor
// (`CapEditorThresholdLadder.tsx`, `step={1}`); validation admits fractions, and against a
// fractional rung a floored integer can understate by up to one point — understating is the
// survivable direction, since it can only make a blocking line look worse, never a live one look
// blocked.
//
// `<1%` rather than `0%` for a month that has been touched but barely: `0%` reads as "nothing has
// been booked here", and that is the same class of lie in the other direction — the one the `null`
// handling at each call site already exists to prevent for an uncapped month.
//
// Returns `null` for a non-finite figure. The CALLER owns that branch — a closed month says
// "closed", a table says "—" — because those labels differ per surface and this rule does not.
//
// TWO exports, one rule. `...Token` is the figure WITHOUT the sign, for a surface whose own string
// already carries it — the portal's i18n entries are `"{{utilization_pct}}% used"`, and a caller
// that interpolated the full `89%` there would render `89%%`. Anything that renders the percentage
// on its own uses `format...` and never re-implements the concatenation.
CONSTANTS.CREDIT_LINE_UTILIZATION_BELOW_ONE_PCT_TOKEN = '<1';

CONSTANTS.creditLineUtilizationPctToken = function (utilization_pct) {
    if (!Number.isFinite(utilization_pct)) {
        return null;
    }

    if (utilization_pct > 0 && utilization_pct < 1) {
        return CONSTANTS.CREDIT_LINE_UTILIZATION_BELOW_ONE_PCT_TOKEN;
    }

    return String(Math.floor(utilization_pct));
};

CONSTANTS.formatCreditLineUtilizationPct = function (utilization_pct) {
    const token = CONSTANTS.creditLineUtilizationPctToken(utilization_pct);

    return token === null ? null : `${token}%`;
};
